import { createPersistDecorator } from '@wkda/funnel-components';
import { DataLocalStorage, isLocalStorageSupported } from '@wkda/funnel-utils';
import { useCallback, useMemo } from 'react';
import { mergeWith } from 'lodash-es';
import { useCarleadConfiguration } from '../hooks/useCarleadCreationConfiguration';
import { DSBDataLocalStorage } from './DSBDataLocalStorage';
import { DSBDataQueryStringStorage } from './DSBDataQueryStringStorage';
import { QueryStringCache } from './QueryStringCache';
import { mapFromQuery } from './queryObjectMapper';
import { parse } from 'query-string';
const mergeObjects = (target, source) => {
  return mergeWith({}, target, source, (targetValue, sourceValue) => {
    if (sourceValue === '') {
      return targetValue;
    }
    // Handle with default mergeWith customizer
    return undefined;
  });
};
export function useCacheStorage() {
  const {
    locale,
    fullLocale,
    featuresFlags: {
      exactMileage
    },
    isPartnerFunnel
  } = useCarleadConfiguration();
  const storage = useMemo(() => {
    function forceQueryStringStorageEnabled() {
      try {
        if (typeof window === 'undefined') return false;
        const qs = parse(window.location.search);
        return isPartnerFunnel && qs['forceQuery'] === 'true';
      } catch (error) {
        return false;
      }
    }
    return isLocalStorageSupported() && !forceQueryStringStorageEnabled() ? new DSBDataLocalStorage(new DataLocalStorage(), {
      locale,
      fullLocale,
      exactMileage
    }) : new DSBDataQueryStringStorage(new QueryStringCache(), {
      locale,
      fullLocale,
      exactMileage
    });
  }, [locale, fullLocale, exactMileage, isPartnerFunnel]);
  const getData = useCallback(function getData() {
    function getDataItemFromQueryString() {
      try {
        if (typeof window === 'undefined') {
          return null;
        }
        const query = new QueryStringCache().getItem(storage.formStorageKey);
        return mapFromQuery(query);
      } catch (e) {
        console.error(`[Form Cache] Failed to get fallback data from query string`, e);
        return null;
      }
    }
    const item = storage.getItem(storage.formStorageKey);
    // See: https://wkdauto.atlassian.net/browse/CFE-17183
    // Partner DSB widgets sets localStorage data on different domain thus we cannot access the values on actual domain after redirect with query params.
    // To prevent this we need to take account the query params when creating preloaded data.
    const queryDataItem = getDataItemFromQueryString();
    try {
      let result = JSON.parse(item);
      if (queryDataItem !== null) {
        result = mergeObjects(queryDataItem, result);
      }
      return result;
    } catch (e) {
      console.error(`[Form Cache] Error getting data: ${item}`, e);
      return null;
    }
  }, [storage]);
  const setData = useCallback(function setData(values) {
    storage.setItem(storage.formStorageKey, JSON.stringify(values));
  }, [storage]);
  const API = useMemo(() => {
    return {
      storage,
      getData,
      setData
    };
  }, [storage, getData, setData]);
  return API;
}
export function usePersistDecorator() {
  const {
    storage
  } = useCacheStorage();
  const persistDecorator = useMemo(() => {
    return createPersistDecorator({
      name: storage.formStorageKey,
      debounceTime: 100,
      // TODO: DSBCoreDataLocalStorage is missing some of the Storage methods
      // Probable it's better to fork `final-form-persist` to make `storage` property more generic
      // Current implementaion is using only `setItem`, `getItem` and `removeItem` methods
      // @ts-expect-error
      storage
    });
  }, [storage]);
  return persistDecorator;
}